import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  studentsControllerGetExams,
  saveUserReferralMapping,
  useStudentsControllerSaveStudentCurrentProgram,
} from "@oneapp/data";
import {
  useAuthStore,
  useIsAuthenticated,
  useAuthActions,
  type ProfilesData,
} from "../../store/useAuthStore";
import { useprofileUpdater } from "../../pages/profile/api";
import { parseLink, parseSignupReferralDeeplink } from "./parseLink";
import { isDeepLinkProfileSame, resolveDeepLinkExamId } from "./profileSync";
import { MODULE_HANDLERS } from "./modules";
import { ILTSDeeplinkHandler, isILTSDeeplink } from "./modules/iltsDeeplink";
import type { ParsedLink } from "./types";
import { ROUTESWEB } from "@repo/utils";

const WEB_DEEPLINK_URL_STORAGE_KEY = "web_deeplink_url";

function getUserFullNameAndUamId(profilesData: ProfilesData | null): {
  userFullName: string;
  userId: number;
} | null {
  if (!profilesData?.unified_account_details) return null;
  const uad = profilesData.unified_account_details.user_account_details;
  const first = (uad?.first_name as string) ?? "";
  const last = (uad?.last_name as string) ?? "";
  const userFullName = `${first} ${last}`.trim() || "User";
  const profiles = profilesData.unified_account_details.profiles;
  if (!Array.isArray(profiles) || profiles.length === 0) return null;
  const profile =
    profiles.find((p) => p.is_default_profile === true) ?? profiles[0];
  const uam = profile?.uam_id;
  if (uam == null) return null;
  const userId = typeof uam === "number" ? uam : Number(uam);
  return Number.isNaN(userId) ? null : { userFullName, userId };
}

function isProfileSame(
  parsed: ParsedLink,
  currentGrade?: number,
  currentExams?: { examId: number; name: string }[],
): boolean {
  return isDeepLinkProfileSame(parsed, currentGrade, currentExams);
}

function isOnDoubtsRoute(pathname: string): boolean {
  const base = `/${ROUTESWEB.APP.DOUBTS}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** Bare /doubts in-app nav: router already on target and no grade/exam to sync. */
function shouldSkipRedundantDoubtsDeeplink(
  parsed: ParsedLink,
  pathname: string,
  currentExams: { examId: number; name: string }[] | undefined,
): boolean {
  if (parsed.module !== "doubts" || !isOnDoubtsRoute(pathname)) return false;
  const resolvedExamId =
    typeof parsed.examName === "string" && parsed.examName
      ? (currentExams ?? []).find((e) => e.name === parsed.examName)?.examId
      : parsed.examName;
  return !parsed.grade || resolvedExamId == null;
}

export const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const profilesData = useAuthStore((s) => s.profilesData);
  const { profileUpdater } = useprofileUpdater();
  const { setIsSyncing } = useAuthActions();
  const { mutateAsync: saveStudentCurrentProgram } =
    useStudentsControllerSaveStudentCurrentProgram({
      request: { clientName: "oneApp" },
    });

  const [incomingUrl, setIncomingUrl] = useState<string | null>(null);
  const inFlightUrl = useRef<string | null>(null);
  const lastSignupDeeplinkHandled = useRef<string | null>(null);
  const referralMappingInFlight = useRef(false);

  const profileUpdaterRef = useRef(profileUpdater);
  const navigateRef = useRef(navigate);
  const saveStudentCurrentProgramRef = useRef(saveStudentCurrentProgram);
  const setIsSyncingRef = useRef(setIsSyncing);
  useEffect(() => {
    profileUpdaterRef.current = profileUpdater;
    navigateRef.current = navigate;
    saveStudentCurrentProgramRef.current = saveStudentCurrentProgram;
    setIsSyncingRef.current = setIsSyncing;
  });

  useEffect(() => {
    setIncomingUrl(window.location.href);
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    if (!incomingUrl) return;
    const hasDeepLinkContext =
      parseLink(incomingUrl) !== null ||
      parseSignupReferralDeeplink(incomingUrl) !== null;
    if (!hasDeepLinkContext) return;
    try {
      localStorage.setItem(WEB_DEEPLINK_URL_STORAGE_KEY, incomingUrl);
    } catch {
      // ignore storage failures (private mode/quota/etc)
    }
  }, [incomingUrl]);

  useEffect(() => {
    if (isAuthenticated || !incomingUrl) return;
    if (lastSignupDeeplinkHandled.current === incomingUrl) return;

    const signupParsed = parseSignupReferralDeeplink(incomingUrl);
    if (!signupParsed) return;

    lastSignupDeeplinkHandled.current = incomingUrl;
    localStorage.setItem("pendingReferralCode", signupParsed.referralCode);

    navigate(`/${ROUTESWEB.AUTH.SIGNUP}`, { replace: true });
  }, [incomingUrl, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const code = localStorage.getItem("pendingReferralCode")?.trim();
    if (!code) return;

    const user = getUserFullNameAndUamId(profilesData);
    if (!user || referralMappingInFlight.current) return;

    referralMappingInFlight.current = true;
    (async () => {
      try {
        await saveUserReferralMapping(
          {
            referalCode: code,
            userFullName: user.userFullName,
            userId: user.userId,
          },
          { clientName: "myActivity" },
        );
        localStorage.removeItem("pendingReferralCode");
      } catch (e) {
        console.error("[DeepLinkHandler] saveUserReferralMapping failed", e);
      } finally {
        referralMappingInFlight.current = false;
      }
    })();
  }, [isAuthenticated, profilesData]);

  useEffect(() => {
    if (!isAuthenticated || !incomingUrl) return;
    if (inFlightUrl.current === incomingUrl) return;

    const currentUrl = incomingUrl;
    const parsed = parseLink(currentUrl);

    if (!parsed) return;

    const currentExams = profilesData?.unified_account_details?.profiles?.[0]
      ?.exams as { examId: number; name: string }[] | undefined;
    if (
      shouldSkipRedundantDoubtsDeeplink(parsed, location.pathname, currentExams)
    ) {
      inFlightUrl.current = currentUrl;
      return;
    }

    inFlightUrl.current = currentUrl;

    const finish = async () => {
      const handler = parsed.module
        ? MODULE_HANDLERS[parsed.module]
        : undefined;
      if (!handler) return;
      try {
        const result = await handler(parsed, navigateRef.current);
        if (result && typeof result === "object" && "path" in result) {
          navigateRef.current(result.path, {
            replace: true,
            state: result.state,
          });
        }
      } catch (handlerError) {
        console.error(
          `[DeepLinkHandler] Handler "${parsed.module}" threw:`,
          handlerError,
        );
        navigateRef.current(`/${ROUTESWEB.APP.DASHBOARD}`, { replace: true });
      }
    };

    const syncAndNavigate = async () => {
      setIsSyncingRef.current(true);
      try {
        const authState = useAuthStore.getState();
        const prof = authState.profilesData;

        const currentGrade =
          prof?.unified_account_details?.user_account_details?.grade?.gradeId;
        const currentExams = prof?.unified_account_details?.profiles?.[0]
          ?.exams as any;
        const uamId = prof?.unified_account_details?.profiles?.[0]?.uam_id;

        const resolvedExamId = resolveDeepLinkExamId(parsed, currentExams);

        if (!parsed.grade || resolvedExamId == null) {
          await finish();
          return;
        }

        if (isProfileSame(parsed, currentGrade, currentExams)) {
          await finish();
          return;
        }

        if (!uamId) {
          navigateRef.current(`/${ROUTESWEB.APP.DASHBOARD}`, { replace: true });
          return;
        }

        const gradeId = Number(parsed.grade);
        if (Number.isNaN(gradeId)) {
          navigateRef.current(`/${ROUTESWEB.APP.DASHBOARD}`, { replace: true });
          return;
        }

        try {
          const res = (await studentsControllerGetExams(
            { verticalId: 1 },
            { clientName: "oneApp" },
          )) as any;

          const gradeData = res?.data?.find(
            (g: any) => Number(g.gradeId) === gradeId,
          );
          const examData = gradeData?.exams?.find(
            (e: any) => e.id === resolvedExamId,
          );

          if (gradeData && examData) {
            await profileUpdaterRef.current(
              {
                uam_id: String(uamId),
                grade_id: gradeId,
                exams: [examData.id],
                exam_ids: [examData.id],
              },
              { manageSyncing: false },
            );

            try {
              const currentAuthState = useAuthStore.getState();
              const enrolledPrograms = currentAuthState.enrolledPrograms;

              if (enrolledPrograms && enrolledPrograms.length > 0) {
                const targetExamId = String(examData.id);
                const targetGradeId = String(gradeId);
                const currentProgram = enrolledPrograms.find(
                  (p) =>
                    (String(p.examId) === targetExamId ||
                      String(p.mdsExamId) === targetExamId) &&
                    String(p.gradeId) === targetGradeId,
                );

                if (currentProgram?.program_id) {
                  try {
                    await saveStudentCurrentProgramRef.current({
                      data: { programId: currentProgram.program_id },
                    });
                  } catch (apiError) {
                    console.error(
                      "[DeepLinkHandler] saveStudentCurrentProgram API failed:",
                      apiError,
                    );
                  }

                  localStorage.setItem(
                    "SELF_LEARN_SELECTED_PROGRAM",
                    JSON.stringify(currentProgram),
                  );

                  const enrolledProgramsWithSelection = enrolledPrograms.map(
                    (p) => ({
                      ...p,
                      isDefault:
                        String(p.program_id) ===
                        String(currentProgram.program_id),
                    }),
                  );

                  useAuthStore
                    .getState()
                    .setEnrolledPrograms(enrolledProgramsWithSelection);
                }
              }
            } catch (programError) {
              console.warn(
                "[DeepLinkHandler] Failed to set current program based on exam:",
                programError,
              );
            }
            await finish();
          } else {
            console.warn(
              "[DeepLinkHandler] Grade/Exam mismatch detected, redirecting to Dashboard",
            );
            navigateRef.current(`/${ROUTESWEB.APP.DASHBOARD}`, {
              replace: true,
            });
          }
        } catch (error) {
          console.error("[DeepLinkHandler] Sync failed:", error);
          navigateRef.current(`/${ROUTESWEB.APP.DASHBOARD}`, { replace: true });
        }
      } finally {
        setIsSyncingRef.current(false);
        if (inFlightUrl.current === currentUrl) {
          inFlightUrl.current = null;
        }
      }
    };

    syncAndNavigate();
  }, [incomingUrl, isAuthenticated, location.pathname, profilesData]);

  if (isAuthenticated && incomingUrl) {
    const parsed = parseLink(incomingUrl);
    if (parsed && isILTSDeeplink(parsed)) {
      return (
        <ILTSDeeplinkHandler
          key={incomingUrl}
          scheduleId={parsed.scheduleId!}
          cohortAsmId={parsed.cohortAsmId!}
          examid={parsed.testId!}
        />
      );
    }
  }

  return null;
};
